import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Context for chat input props from Chatty to AppContent
const ChatInputContext = createContext({
  chatInputProps: null,
  setChatInputProps: () => {},
  sidebarProps: null,
  setSidebarProps: () => {},
  sessionsRefreshTrigger: 0,
  refreshSessions: () => {},
  qnaData: null,
  setQnaData: () => {},
});

// Provider component to wrap AppContent
export const ChatInputProvider = ({ children }) => {
  const [chatInputProps, setChatInputProps] = useState(null);
  const [sidebarProps, setSidebarProps] = useState(null);
  const [sessionsRefreshTrigger, setSessionsRefreshTrigger] = useState(0);
  const [qnaData, setQnaData] = useState(null); // { questions, onSubmit, onDismiss }

  // Call this to trigger a refresh of sessions in App.js sidebar
  const refreshSessions = useCallback(() => {
    setSessionsRefreshTrigger(prev => prev + 1);
  }, []);

  // Optimistic single-session bump — used by useChatSend after a successful
  // message round-trip. Mirrors backend's `last_accessed` save without
  // re-fetching the entire list (no flicker, no view-bump amplification).
  // App.js exports its setChatSessions setter into setChatSessionsRef on mount;
  // bumpSessionToTop calls it to update the in-memory state that ChatSidebar
  // renders from.
  const setChatSessionsRef = useRef(null);
  const registerSetChatSessions = useCallback((setter) => {
    setChatSessionsRef.current = setter;
  }, []);
  const bumpSessionToTop = useCallback((sessionId) => {
    const setter = setChatSessionsRef.current;
    if (!setter || !sessionId) return () => {};
    const now = new Date().toISOString();

    // Capture prev in-memory + prev localStorage value once, idempotent
    // under React StrictMode's double-call setter pattern. The captured
    // closure is what the returned revert function uses on send failure
    // so the sidebar settles to the real backend state after a network
    // error / abort / 5xx — not stuck on the optimistic bump.
    const captured = { prevTs: undefined };
    setter(prev => {
      if (captured.prevTs === undefined) {
        const found = prev.find(s => s.session_id === sessionId);
        captured.prevTs = found ? (found.last_accessed || null) : null;
      }
      return prev.map(s =>
        s.session_id === sessionId ? { ...s, last_accessed: now } : s
      );
    });

    let prevLocal = undefined;
    try {
      const raw = localStorage.getItem('chatty_session_bumps') || '{}';
      const map = JSON.parse(raw);
      prevLocal = Object.prototype.hasOwnProperty.call(map, sessionId) ? map[sessionId] : null;
      map[sessionId] = now;
      localStorage.setItem('chatty_session_bumps', JSON.stringify(map));
    } catch (_) { prevLocal = null; }

    // Revert handle — caller invokes on send failure so the optimistic
    // bump is undone and the sidebar reflects what the backend actually
    // saved (i.e. nothing for this attempt).
    return () => {
      if (captured.prevTs !== undefined) {
        const restoreTs = captured.prevTs;
        setter(prev => prev.map(s =>
          s.session_id === sessionId
            ? (restoreTs === null ? (() => { const { last_accessed, ...rest } = s; return rest; })() : { ...s, last_accessed: restoreTs })
            : s
        ));
      }
      try {
        const raw = localStorage.getItem('chatty_session_bumps') || '{}';
        const map = JSON.parse(raw);
        if (prevLocal === null || prevLocal === undefined) {
          delete map[sessionId];
        } else {
          map[sessionId] = prevLocal;
        }
        localStorage.setItem('chatty_session_bumps', JSON.stringify(map));
      } catch (_) { /* nothing to restore on storage failure */ }
    };
  }, []);
  // New session created — insert at top of App.js's chatSessions so the
  // sidebar shows it immediately without a list refetch.
  const addSessionToTop = useCallback((newSession) => {
    const setter = setChatSessionsRef.current;
    if (!setter || !newSession?.session_id) return;
    setter(prev => [newSession, ...prev.filter(s => s.session_id !== newSession.session_id)]);
  }, []);
  // Local-only delete — mirror to App.js's chatSessions so the sidebar
  // updates without refetching.
  const removeSession = useCallback((sessionId) => {
    const setter = setChatSessionsRef.current;
    if (!setter || !sessionId) return;
    setter(prev => prev.filter(s => s.session_id !== sessionId));
  }, []);
  // Local-only patch — mirror title/project rename etc. to App.js's state.
  const patchSession = useCallback((sessionId, patch) => {
    const setter = setChatSessionsRef.current;
    if (!setter || !sessionId || !patch) return;
    setter(prev => prev.map(s =>
      s.session_id === sessionId ? { ...s, ...patch } : s
    ));
  }, []);

  return (
    <ChatInputContext.Provider value={{
      chatInputProps,
      setChatInputProps,
      sidebarProps,
      setSidebarProps,
      sessionsRefreshTrigger,
      refreshSessions,
      qnaData,
      setQnaData,
      registerSetChatSessions,
      bumpSessionToTop,
      addSessionToTop,
      removeSession,
      patchSession,
    }}>
      {children}
    </ChatInputContext.Provider>
  );
};

// Hook to access chat input context
export const useChatInput = () => useContext(ChatInputContext);